import { useEffect, useMemo, useState, useCallback } from "react";
import { useGo, useTranslation, CanAccess, useList } from "@refinedev/core";
import { FaChevronLeft, FaCalendar, FaClock, FaUser } from "react-icons/fa6";
import { useEvent } from "core/events/createEvent";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { useProfessionalCalendar } from "hooks/appointment/useProfessionalCalendar";
import dayjs from "dayjs";
import { GoogleServicesTemplate } from "@components/templates/googleServices";
import { ProfessionalSelection, DateSelection, TimeSelection } from "./components";
import { AppointmentStep, ProfessionalPublicCalendar } from "./types";
import { PlansPopup } from "@components/molecules/PlansPopup";
import { ServiceOrPlanChoice } from "@components/molecules/ServiceOrPlanChoice";
import { ServicePopup } from "@components/molecules/ServicePopup";
import { useAtomValue } from "jotai";
import { roleAtom } from "../../auth/firebase/state";
import { useServicePurchase } from "../../hooks/useServicePurchase";

export const AppointProfessional = () => {
    const calendarId = useAppCalendar()
    const currentDate = useMemo(() => dayjs(), [])
    const [currentStep, setCurrentStep] = useState<AppointmentStep>('professional')
    const [selectedDate, setSelectedDate] = useState(currentDate.startOf('day'))
    const [selectedTime, setSelectedTime] = useState<Date | null>(null)
    const [selectedProfessionalIndex, setSelectedProfessionalIndex] = useState(-1)
    const [professionalsData, setProfessionalsData] = useState<ProfessionalPublicCalendar[]>([])
    const go = useGo()

    const { role } = useAtomValue(roleAtom);
    const [showService, setShowService] = useState(false);
    const [showServiceChoice, setShowServiceChoice] = useState(false);

    // Get consultation feature ID and check if user has purchased it
    const { data: features } = useList({
        resource: "features",
        filters: [{ field: "name", operator: "eq", value: "consultation" }],
        pagination: { current: 1, pageSize: 1 },
        meta: { fields: ["id", "name"] }
    });
    
    const consultationFeatureId = features?.data?.[0]?.id;
    const { hasPurchased, isLoading: purchaseLoading } = useServicePurchase(consultationFeatureId ? String(consultationFeatureId) : undefined);

    const startDate = useMemo(() => dayjs(selectedDate).isBefore(currentDate) ? currentDate.startOf('day') : dayjs(selectedDate).startOf('month'), [selectedDate, currentDate])
    const endDate = useMemo(() => dayjs(selectedDate).endOf('month'), [selectedDate])

    // Get the selected professional from the real data
    const selectedProfessional = selectedProfessionalIndex >= 0 && professionalsData.length > 0
        ? professionalsData[selectedProfessionalIndex]
        : undefined

    // Get calendar IDs from the selected professional
    const availabilityCalendarIds = useMemo(() => {
        if (!selectedProfessional?.availability_calendar_id) return []
        return [selectedProfessional.availability_calendar_id]
    }, [selectedProfessional?.availability_calendar_id])

    const appointmentsCalendarIds = useMemo(() => {
        if (!selectedProfessional?.appointments_calendar_id) return []
        return [selectedProfessional.appointments_calendar_id]
    }, [selectedProfessional?.appointments_calendar_id])

    const {
        data: availabilitySlots,
        isLoading: isLoadingAvailability,
    } = useProfessionalCalendar(availabilityCalendarIds, appointmentsCalendarIds, startDate, endDate)

    // TEMPORAL: Datos de prueba para disponibilidad
    const mockAvailabilitySlots = useMemo(() => {
        if (selectedProfessional && (!availabilitySlots || availabilitySlots[0]?.length === 0)) {
            const slots = []
            const today = dayjs().startOf('day')
            
            // Generar slots para los próximos 7 días
            for (let day = 0; day < 7; day++) {
                const currentDay = today.add(day, 'day')
                
                // Generar slots de 9 AM a 5 PM cada hora
                for (let hour = 9; hour < 17; hour++) {
                    slots.push(currentDay.hour(hour).minute(0).second(0).toDate())
                }
            }
            
            return [slots]
        }
        return availabilitySlots
    }, [availabilitySlots, selectedProfessional])

    const { translate } = useTranslation()
    const { createAppointmentEvent, isLoading: isLoadingCreateEvent, isSuccess: isSuccessCreateEvent } = useEvent()

    const availabilitySlotsSelected = mockAvailabilitySlots?.[0]?.filter(date => dayjs(date).isAfter(currentDate))
    const selectedDaySlots = availabilitySlotsSelected?.filter(date => dayjs(date).format("YYYY-MM-DD") == dayjs(selectedDate).format("YYYY-MM-DD"))

    const createEvent = async () => {
        if (!selectedTime || !selectedProfessional) {
            console.error('Missing required data:', { selectedTime, selectedProfessional });
            return;
        }

        // Para user_band0, verificar si ya compró el servicio
        if (role === 'user_band0') {
            if (purchaseLoading) return; // Wait for purchase check
            
            if (!hasPurchased) {
                // Mostrar componente de compra
                setShowServiceChoice(true);
                return;
            }
        }

        console.log('Selected professional data:', selectedProfessional);
        console.log('Professional user data:', selectedProfessional.user_professional_public);

        try {
            const startTime = dayjs(selectedTime)
            const startDate = dayjs(selectedDate)
                .hour(startTime.hour())
                .minute(startTime.minute())
                .second(startTime.second());
            
            await createAppointmentEvent(startDate, selectedProfessional.user_professional_public)
        } catch (error: any) {
            console.error('Error creating appointment:', error);
            
            // Si el error es por falta de refresh token, mostrar mensaje específico
            if (error?.message?.includes('no professional refresh token found')) {
                alert('El profesional seleccionado no tiene configurado su calendario. Por favor selecciona otro profesional o contacta al soporte.');
            } else {
                alert('Error al crear la cita. Por favor intenta nuevamente.');
            }
        }
    }

    const handleProfessionalSelect = (index: number) => {
        setSelectedProfessionalIndex(index)
    }

    const handleProfessionalContinue = () => {
        setCurrentStep('date')
    }

    const handleDateSelect = useCallback((date: dayjs.Dayjs) => {
        setSelectedDate(date)
        setSelectedTime(null)
    }, [])

    const handleDateContinue = () => {
        setCurrentStep('time')
    }

    const handlePageChange = useCallback((date: string) => {
        setSelectedDate(dayjs(date))
    }, [])

    const handleTimeSelect = (time: Date) => {
        setSelectedTime(time)
    }

    const goBack = () => {
        switch (currentStep) {
            case 'date':
                setCurrentStep('professional')
                break
            case 'time':
                setCurrentStep('date')
                break
        }
    }

    useEffect(() => {
        if (isSuccessCreateEvent) {
            go({ to: { resource: 'home', action: 'list' } })
        }
    }, [isSuccessCreateEvent])

    // Mobile header showing previous selections
    const MobileHeader = () => {
        if (currentStep === 'professional') return null

        return (
            <header className="fixed top-0 left-0 right-0 z-50 bg-base-100 lg:hidden">
                <div className="flex items-center justify-between p-4">
                    <button
                        onClick={goBack}
                        className="btn btn-ghost btn-sm"
                        aria-label={translate("page.appointments.actions.goBack")}
                    >
                        <FaChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-3">
                        {selectedProfessional && (
                            <div className="flex items-center gap-2">
                                <img
                                    src={selectedProfessional.user_professional_public.avatar_url || '/img/default-avatar.png'}
                                    alt={selectedProfessional.user_professional_public.display_name || 'Professional'}
                                    className="w-8 h-8 rounded-full object-cover"
                                />
                                <span className="text-sm font-medium truncate max-w-32">
                                    {selectedProfessional.user_professional_public.display_name}
                                </span>
                            </div>
                        )}
                        {currentStep === 'time' && selectedDate && (
                            <div className="flex items-center gap-1 text-sm text-base-content/70">
                                <FaCalendar className="w-3 h-3" />
                                <span>{dayjs(selectedDate).format("MMM D")}</span>
                            </div>
                        )}
                    </div>
                </div>
            </header>
        )
    }

    // Step indicator component
    const StepIndicator = () => {
        const handleStepClick = (step: 'professional' | 'date' | 'time') => {
            // Only allow navigation to steps that have been completed or are accessible
            if (step === 'professional') {
                setCurrentStep('professional')
            } else if (step === 'date' && selectedProfessional) {
                setCurrentStep('date')
            } else if (step === 'time' && selectedProfessional && selectedDate) {
                setCurrentStep('time')
            }
        }

        return (
            <div className="flex justify-center mb-8">
                <ul className="steps steps-horizontal">
                    <li 
                        className={`step ${currentStep === 'professional' ? 'step-primary' : 'step-neutral'} ${selectedProfessional ? 'cursor-pointer hover:step-primary' : ''}`}
                        onClick={() => handleStepClick('professional')}
                    >
                        <span className="step-icon">
                            <FaUser className="w-4 h-4" />
                        </span>
                        <span className="hidden sm:inline">{translate("page.appointments.steps.professional")}</span>
                    </li>
                    <li 
                        className={`step ${currentStep === 'date' ? 'step-primary' : currentStep === 'time' ? 'step-neutral' : 'step-neutral'} ${selectedProfessional ? 'cursor-pointer hover:step-primary' : 'cursor-not-allowed'}`}
                        onClick={() => handleStepClick('date')}
                    >
                        <span className="step-icon">
                            <FaCalendar className="w-4 h-4" />
                        </span>
                        <span className="hidden sm:inline">{translate("page.appointments.steps.date")}</span>
                    </li>
                    <li 
                        className={`step ${currentStep === 'time' ? 'step-primary' : 'step-neutral'} ${selectedProfessional && selectedDate ? 'cursor-pointer hover:step-primary' : 'cursor-not-allowed'}`}
                        onClick={() => handleStepClick('time')}
                    >
                        <span className="step-icon">
                            <FaClock className="w-4 h-4" />
                        </span>
                        <span className="hidden sm:inline">{translate("page.appointments.steps.time")}</span>
                    </li>
                </ul>
            </div>
        )
    }

    // Mostrar ServiceOrPlanChoice cuando el usuario intenta agendar sin haber comprado
    if (showServiceChoice) {
        return (
            <GoogleServicesTemplate>
                <ServiceOrPlanChoice 
                    onUseService={() => {
                        setShowServiceChoice(false);
                        setShowService(true);
                    }}
                    serviceTitle="Comprar servicio"
                    serviceDescription="Programa una consulta de 1 hora con un profesional de tu elección"
                    serviceIcon={<FaCalendar />}
                    serviceButtonText="Continuar con la compra"
                    serviceType="consultation"
                />
            </GoogleServicesTemplate>
        );
    }

    // Mostrar ServicePopup cuando el usuario está comprando el servicio
    if (showService) {
        return (
            <GoogleServicesTemplate>
                <ServicePopup 
                    onBack={() => setShowService(false)}
                    serviceType="consultation"
                />
            </GoogleServicesTemplate>
        );
    }

    return (
        <GoogleServicesTemplate>
            <CanAccess 
                resource="appointment" 
                action="show" 
                params={{ validateSubscription: true }}
                fallback={<PlansPopup />}
            >
                <MobileHeader />

            <main className="flex flex-col items-center w-full pt-6 lg:pt-4">
                <section className="w-full max-w-7xl px-4">

                    {/* Desktop: Show all steps as columns */}
                    <div className="hidden lg:grid lg:grid-cols-3 lg:gap-8 lg:min-h-[600px]">
                        {/* Step 1: Professional Selection */}
                        <div className="flex flex-col lg:max-h-[92vh]">
                            <div className="flex items-center gap-3 mb-6">
                                <FaUser className="w-6 h-6 text-secondary" />
                                <h2 className="text-xl font-semibold">{translate("page.appointments.steps.selectProfessional")}</h2>
                            </div>

                            <ProfessionalSelection
                                onProfessionalSelect={handleProfessionalSelect}
                                onProfessionalContinue={handleProfessionalContinue}
                                selectedProfessionalIndex={selectedProfessionalIndex}
                                isLoadingCreateEvent={isLoadingCreateEvent}
                                calendarId={calendarId}
                                onProfessionalsDataChange={setProfessionalsData}
                            />
                        </div>

                        {/* Step 2: Date Selection */}
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-6">
                                <FaCalendar className="w-6 h-6 text-secondary" />
                                <h2 className="text-xl font-semibold">{translate("page.appointments.steps.selectDate")}</h2>
                            </div>

                            <DateSelection
                                selectedProfessional={selectedProfessional}
                                selectedDate={selectedDate}
                                onDateSelect={handleDateSelect}
                                onDateContinue={handleDateContinue}
                                onPageChange={handlePageChange}
                                availabilitySlotsSelected={availabilitySlotsSelected}
                                isDisabled={selectedProfessionalIndex === -1}
                            />
                        </div>

                        {/* Step 3: Time Selection */}
                        <div className="flex flex-col lg:max-h-[92vh]">
                            <div className="flex items-center gap-3 mb-6">
                                <FaClock className="w-6 h-6 text-secondary" />
                                <h2 className="text-xl font-semibold">{translate("page.appointments.steps.selectTime")}</h2>
                            </div>

                            <TimeSelection
                                selectedDate={selectedDate}
                                selectedTime={selectedTime}
                                selectedDaySlots={selectedDaySlots}
                                onTimeSelect={handleTimeSelect}
                                onBookAppointment={createEvent}
                                isLoadingCreateEvent={isLoadingCreateEvent}
                                isDisabled={selectedProfessionalIndex === -1}
                            />
                        </div>
                    </div>

                    {/* Mobile: Show one step at a time */}
                    <div className="lg:hidden pb-6">
                        <StepIndicator />

                        {/* Step 1: Professional Selection */}
                        {currentStep === 'professional' && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <FaUser className="w-6 h-6 text-secondary" />
                                    <h2 className="text-xl font-semibold">{translate("page.appointments.steps.selectProfessional")}</h2>
                                </div>

                                <ProfessionalSelection
                                    onProfessionalSelect={handleProfessionalSelect}
                                    onProfessionalContinue={handleProfessionalContinue}
                                    selectedProfessionalIndex={selectedProfessionalIndex}
                                    isLoadingCreateEvent={isLoadingCreateEvent}
                                    calendarId={calendarId}
                                    onProfessionalsDataChange={setProfessionalsData}
                                    mobile
                                />
                            </div>
                        )}

                        {/* Step 2: Date Selection */}
                        {currentStep === 'date' && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <FaCalendar className="w-6 h-6 text-secondary" />
                                    <h2 className="text-xl font-semibold">{translate("page.appointments.steps.selectDate")}</h2>
                                </div>

                                <DateSelection
                                    selectedProfessional={selectedProfessional}
                                    selectedDate={selectedDate}
                                    onDateSelect={handleDateSelect}
                                    onDateContinue={handleDateContinue}
                                    onPageChange={handlePageChange}
                                    availabilitySlotsSelected={availabilitySlotsSelected}
                                    isDisabled={false}
                                />
                            </div>
                        )}

                        {/* Step 3: Time Selection */}
                        {currentStep === 'time' && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <FaClock className="w-6 h-6 text-secondary" />
                                    <h2 className="text-xl font-semibold">{translate("page.appointments.steps.selectTime")}</h2>
                                </div>

                                <TimeSelection
                                    selectedDate={selectedDate}
                                    selectedTime={selectedTime}
                                    selectedDaySlots={selectedDaySlots}
                                    onTimeSelect={handleTimeSelect}
                                    onBookAppointment={createEvent}
                                    isLoadingCreateEvent={isLoadingCreateEvent}
                                    isDisabled={false}
                                />
                            </div>
                        )}
                    </div>
                </section>
            </main>
            </CanAccess>
        </GoogleServicesTemplate>
    );
} 